import { spawn, ChildProcess, execSync } from "child_process";
import { fullCleanCwd, getUserFileExtension } from "../helpers/fs.helpers";
import { lastLoadedEnvFiles } from "../dotenv.helpers";
import fs from "fs";
import path from "path";
import sheu from "../sheu";
import portAndHostAllocator from "../features/port-and-host-allocator";
import watermarkStamper from "./utils/watermark-stamper";
import { getArkosConfig } from "../helpers/arkos-config.helpers";

interface DevOptions {
  port?: string;
  host?: string;
}

let child: ChildProcess | null = null;

/**
 * Dev server command for the arkos CLI
 */
export async function devCommand(options: DevOptions = {}) {
  process.env.NO_CLI = "true";

  if (!process.env.NODE_ENV) process.env.NODE_ENV = "development";
  child = null;
  let restartTimeout: NodeJS.Timeout | null = null;

  try {
    const { port, host } = options;

    const config = getArkosConfig();

    const entryPoint = path.resolve(process.cwd(), config.source?.entryPoint!);

    if (!fs.existsSync(entryPoint)) {
      console.error(
        `Could not find application entry point at ${fullCleanCwd(entryPoint).replaceAll(/^\/+/g, "")}`
      );
      return process.exit(1);
    }

    const fileExt = getUserFileExtension();

    const entryPointRelative = path.relative(process.cwd(), entryPoint);

    const baseServiceTypesPath = path.resolve(
      process.cwd(),
      `.arkos/index.d.ts`
    );
    if (
      fileExt === "ts" &&
      !fs.existsSync(baseServiceTypesPath) &&
      fs.existsSync(path.resolve(path.join(process.cwd(), "prisma")))
    ) {
      console.info("\nGenerating arkos types and prisma client...");
      console.log("");
      execSync(`npx arkos prisma generate`);
    }

    const getEnv = () =>
      ({
        NODE_ENV: "development",
        ...process.env,
        ...(port && { CLI_PORT: port }),
        ...(host && { CLI_HOST: host }),
        CLI: "false",
      }) as { [x: string]: string; };

    const env = getEnv();
    const hostAndPort = await portAndHostAllocator.getHostAndAvailablePort(
      env,
      { logWarning: true }
    );

    watermarkStamper.stamp({
      ...hostAndPort,
      envFiles: lastLoadedEnvFiles,
    });

    process.env.__PORT = hostAndPort.port || "";
    process.env.__HOST = hostAndPort.host || "";

    const startServer = () => {
      if (child) {
        child.kill();
        child = null;
      }

      const env = getEnv();

      if (fileExt === "ts") {
        child = spawn("npx", ["tsx-strict", "--watch", entryPointRelative], {
          stdio: ["inherit", "inherit", "inherit", "ipc"],
          env,
        });
      } else {
        env.TSX_TSCONFIG_PATH = "./jsconfig.json";
        child = spawn(
          "npx",
          ["tsx-strict", "--no-type-check", "--watch", entryPointRelative],
          {
            stdio: ["inherit", "inherit", "inherit", "ipc"],
            env,
          }
        );
      }

      if (child) {
        child.on("error", (error) => {
          console.error("Failed to start server:", error);
        });
      }
    };

    startServer();

    const cleanup = () => {
      if (restartTimeout) clearTimeout(restartTimeout);

      if (child) {
        child.kill("SIGTERM");

        setTimeout(() => {
          if (child && !child.killed) child.kill("SIGKILL");
        }, 5000);
      }

      process.exit(0);
    };

    process.on("SIGINT", cleanup);
    process.on("SIGTERM", cleanup);

    process.on("uncaughtException", (error) => {
      console.error("Uncaught Exception:", error);
      cleanup();
    });
  } catch (error) {
    sheu.error("Development server failed to start:");
    console.error(error);

    if (child) {
      (child as ChildProcess)?.kill?.();
      child = null;
    }

    process.exit(1);
  }
}

/**
 * Help function to help other processes to terminate the development server child process
 */
export function killDevelopmentServerChildProcess() {
  (child as ChildProcess)?.kill?.();
  child = null;
}
